'use client';
import { motion, useScroll, useTransform } from 'motion/react';
import { useRef } from 'react';
import { useI18n, useDirection } from '@/i18n';
import {
  Shield,
  TrendingDown,
  Users,
  Sparkles,
  GraduationCap,
  ArrowRight,
  ArrowLeft,
} from 'lucide-react';

const featureData = [
  {
    icon: Shield,
    gradient: 'from-emerald-500/20 via-teal-500/10 to-transparent',
    accentColor: 'text-emerald-600',
    bgAccent: 'bg-emerald-500',
  },
  {
    icon: TrendingDown,
    gradient: 'from-rose-500/20 via-pink-500/10 to-transparent',
    accentColor: 'text-rose-600',
    bgAccent: 'bg-rose-500',
  },
  {
    icon: Users,
    gradient: 'from-blue-500/20 via-indigo-500/10 to-transparent',
    accentColor: 'text-blue-600',
    bgAccent: 'bg-blue-500',
  },
  {
    icon: Sparkles,
    gradient: 'from-violet-500/20 via-purple-500/10 to-transparent',
    accentColor: 'text-violet-600',
    bgAccent: 'bg-violet-500',
  },
  {
    icon: GraduationCap,
    gradient: 'from-amber-500/20 via-orange-500/10 to-transparent',
    accentColor: 'text-amber-600',
    bgAccent: 'bg-amber-500',
  },
];

// Mockup UI components for each feature
function QualityMockup() {
  const { t } = useI18n();
  const m = t.features.mockup.quality;
  return (
    <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden bg-gradient-to-br from-background to-muted/50 border border-border shadow-2xl">
      {/* Header bar */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-muted/30">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-400" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" />
          <div className="w-3 h-3 rounded-full bg-green-400" />
        </div>
        <div className="flex-1 flex justify-center">
          <div className="px-4 py-1 rounded-md bg-muted text-xs text-muted-foreground">
            {m.title}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-4">
        {/* Score header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-muted-foreground mb-1">{m.overallScore}</div>
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, type: 'spring' }}
              className="text-4xl font-display font-semibold text-foreground"
            >
              92<span className="text-lg text-muted-foreground">/100</span>
            </motion.div>
          </div>
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5, type: 'spring' }}
            className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center"
          >
            <Shield className="w-8 h-8 text-emerald-600" />
          </motion.div>
        </div>

        {/* Progress bars */}
        <div className="space-y-3 pt-4">
          {[
            { label: m.communication, value: 95 },
            { label: m.problemResolution, value: 88 },
            { label: m.productKnowledge, value: 92 },
            { label: m.compliance, value: 100 },
          ].map((item, i) => (
            <div key={item.label} className="space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">{item.label}</span>
                <span className="font-medium text-foreground">{item.value}%</span>
              </div>
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: `${item.value}%` }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: 0.4 + i * 0.1 }}
                  className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-400"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DSATMockup() {
  const { t } = useI18n();
  const m = t.features.mockup.dsat;
  return (
    <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden bg-gradient-to-br from-background to-muted/50 border border-border shadow-2xl">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-muted/30">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-400" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" />
          <div className="w-3 h-3 rounded-full bg-green-400" />
        </div>
        <div className="flex-1 flex justify-center">
          <div className="px-4 py-1 rounded-md bg-muted text-xs text-muted-foreground">
            {m.title}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Chart */}
        <div className="flex items-end justify-around h-40 mb-4">
          {[
            { label: m.process, value: 42, color: 'from-rose-500 to-pink-400' },
            { label: m.agent, value: 28, color: 'from-rose-400 to-pink-300' },
            { label: m.system, value: 18, color: 'from-rose-300 to-pink-200' },
            { label: m.external, value: 12, color: 'from-rose-200 to-pink-100' },
          ].map((item, i) => (
            <div key={item.label} className="flex flex-col items-center gap-2">
              <motion.div
                initial={{ height: 0 }}
                whileInView={{ height: `${item.value * 2.5}px` }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 + i * 0.1 }}
                className={`w-12 rounded-t-lg bg-gradient-to-t ${item.color}`}
              />
              <span className="text-xs text-muted-foreground">{item.label}</span>
              <span className="text-sm font-semibold text-foreground">{item.value}%</span>
            </div>
          ))}
        </div>

        {/* Alert card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
          className="p-3 rounded-lg bg-rose-500/5 border border-rose-500/20"
        >
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
            <span className="text-xs font-medium text-rose-600">
              {m.alert}
            </span>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function WFMMockup() {
  const { t } = useI18n();
  const m = t.features.mockup.wfm;
  return (
    <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden bg-gradient-to-br from-background to-muted/50 border border-border shadow-2xl">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-muted/30">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-400" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" />
          <div className="w-3 h-3 rounded-full bg-green-400" />
        </div>
        <div className="flex-1 flex justify-center">
          <div className="px-4 py-1 rounded-md bg-muted text-xs text-muted-foreground">
            {m.title}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          {[
            { label: m.utilization, value: '87%', trend: '+5%' },
            { label: m.slaMet, value: '94%', trend: '+2%' },
            { label: m.costPerContact, value: '$4.20', trend: '-8%' },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 + i * 0.1 }}
              className="p-3 rounded-lg bg-muted/50 text-center"
            >
              <div className="text-lg font-semibold text-foreground">{stat.value}</div>
              <div className="text-xs text-muted-foreground">{stat.label}</div>
              <div className="text-xs text-emerald-600 font-medium">{stat.trend}</div>
            </motion.div>
          ))}
        </div>

        {/* Chart area */}
        <div className="relative h-28">
          <svg viewBox="0 0 300 100" className="w-full h-full" preserveAspectRatio="none">
            {/* Grid */}
            {[0, 25, 50, 75, 100].map((y) => (
              <line key={y} x1="0" y1={y} x2="300" y2={y} stroke="currentColor" strokeWidth="0.5" className="text-border" />
            ))}
            {/* Area fill */}
            <motion.path
              d="M 0,100 L 0,70 Q 50,60 100,50 T 200,35 T 300,25 L 300,100 Z"
              className="fill-blue-500/10"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
            />
            {/* Line */}
            <motion.path
              d="M 0,70 Q 50,60 100,50 T 200,35 T 300,25"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              className="text-blue-500"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.5, delay: 0.3 }}
            />
          </svg>
        </div>
      </div>
    </div>
  );
}

function AIMockup() {
  const { t } = useI18n();
  const m = t.features.mockup.ai;
  return (
    <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden bg-gradient-to-br from-background to-muted/50 border border-border shadow-2xl">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-muted/30">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-400" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" />
          <div className="w-3 h-3 rounded-full bg-green-400" />
        </div>
        <div className="flex-1 flex justify-center">
          <div className="px-4 py-1 rounded-md bg-muted text-xs text-muted-foreground">
            {m.title}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-3">
        {[
          { label: m.churnRisk, value: m.high, confidence: 94, alert: true },
          { label: m.nextBestAction, value: m.coaching, confidence: 89, alert: false },
          { label: m.dsatProbability, value: '23%', confidence: 91, alert: true },
          { label: m.performanceTrend, value: m.improving, confidence: 87, alert: false },
        ].map((item, i) => (
          <motion.div
            key={item.label}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 + i * 0.1 }}
            className={`flex items-center justify-between p-3 rounded-lg border ${item.alert
                ? 'bg-violet-500/5 border-violet-500/20'
                : 'bg-muted/30 border-border'
              }`}
          >
            <div className="flex items-center gap-3">
              {item.alert && (
                <motion.span
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="w-2 h-2 rounded-full bg-violet-500"
                />
              )}
              <span className="text-sm text-muted-foreground">{item.label}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-sm font-medium ${item.alert ? 'text-violet-600' : 'text-foreground'}`}>
                {item.value}
              </span>
              <span className="text-xs text-muted-foreground/60 tabular-nums">
                {item.confidence}%
              </span>
            </div>
          </motion.div>
        ))}

        {/* Sparkle effect */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          className="absolute -bottom-10 -right-10 w-32 h-32 bg-gradient-to-br from-violet-500/20 to-transparent rounded-full blur-2xl"
        />
      </div>
    </div>
  );
}

function QuizMockup() {
  const { t } = useI18n();
  const m = t.features.mockup.quiz;
  return (
    <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden bg-gradient-to-br from-background to-muted/50 border border-border shadow-2xl">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-muted/30">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-400" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" />
          <div className="w-3 h-3 rounded-full bg-green-400" />
        </div>
        <div className="flex-1 flex justify-center">
          <div className="px-4 py-1 rounded-md bg-muted text-xs text-muted-foreground">
            {m.title}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Progress bar */}
        <div className="mb-4">
          <div className="flex justify-between text-xs mb-2">
            <span className="text-muted-foreground">{m.questionProgress}</span>
            <span className="font-medium text-foreground">70%</span>
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: '70%' }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.3 }}
              className="h-full rounded-full bg-gradient-to-r from-amber-500 to-orange-400"
            />
          </div>
        </div>

        {/* Question */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="mb-4"
        >
          <p className="text-sm font-medium text-foreground mb-4">
            {m.sampleQuestion}
          </p>

          {/* Answer options */}
          <div className="space-y-2">
            {[
              { label: 'A', text: '100 Mbps', correct: false },
              { label: 'B', text: '500 Mbps', correct: true, selected: true },
              { label: 'C', text: '1 Gbps', correct: false },
              { label: 'D', text: '2 Gbps', correct: false },
            ].map((option, i) => (
              <motion.div
                key={option.label}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 + i * 0.1 }}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${option.selected && option.correct
                    ? 'bg-emerald-500/5 border-emerald-500/40'
                    : 'bg-muted/30 border-border'
                  }`}
              >
                <div
                  className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium ${option.selected && option.correct
                      ? 'bg-emerald-500 text-white'
                      : 'bg-muted text-muted-foreground'
                    }`}
                >
                  {option.label}
                </div>
                <span className={`text-sm ${option.selected && option.correct ? 'text-emerald-600 font-medium' : 'text-muted-foreground'}`}>
                  {option.text}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Leaderboard preview */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.9 }}
          className="mt-4 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <GraduationCap className="w-4 h-4 text-amber-600" />
              <span className="text-xs font-medium text-amber-600">{m.topPerformer}</span>
            </div>
            <span className="text-xs text-amber-600 font-semibold">🏆 #1</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

const mockups = [QualityMockup, DSATMockup, WFMMockup, AIMockup, QuizMockup];

// Individual feature section
function FeatureSection({
  feature,
  index,
  isReversed,
}: {
  feature: {
    title: string;
    description: string;
    features: string[];
  };
  index: number;
  isReversed: boolean;
}) {
  const { t } = useI18n();
  const { isRTL } = useDirection();
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);

  const data = featureData[index];
  const Icon = data.icon;
  const Mockup = mockups[index];

  return (
    <motion.div
      ref={ref}
      className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${isReversed ? 'lg:grid-flow-dense' : ''
        }`}
    >
      {/* Content */}
      <motion.div
        style={{ opacity }}
        className={isReversed ? 'lg:col-start-2' : ''}
      >
        {/* Feature number */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="flex items-center gap-4 mb-6"
        >
          <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${data.gradient} flex items-center justify-center`}>
            <Icon className={`w-6 h-6 ${data.accentColor}`} />
          </div>
          <span className="font-mono text-sm text-muted-foreground">
            0{index + 1}
          </span>
        </motion.div>

        {/* Title */}
        <motion.h3
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-3xl sm:text-4xl lg:text-5xl font-display font-medium text-foreground tracking-tight mb-6"
        >
          {feature.title}
        </motion.h3>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="text-lg text-muted-foreground leading-relaxed mb-8"
        >
          {feature.description}
        </motion.p>

        {/* Feature list */}
        <motion.ul
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="space-y-4 mb-8"
        >
          {feature.features.map((item, i) => (
            <li key={i} className="flex items-start gap-4">
              <span className={`mt-1.5 w-1.5 h-1.5 rounded-full ${data.bgAccent}`} />
              <span className="text-muted-foreground">{item}</span>
            </li>
          ))}
        </motion.ul>

        {/* CTA */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          whileHover={{ gap: '12px' }}
          className="inline-flex items-center gap-2 text-foreground font-medium group"
        >
          <span>{t.features.learnMoreAbout} {feature.title}</span>
          <ArrowIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </motion.button>
      </motion.div>

      {/* Mockup */}
      <motion.div
        style={{ y }}
        className={isReversed ? 'lg:col-start-1 lg:row-start-1' : ''}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          {/* Glow effect */}
          <div className={`absolute inset-0 bg-gradient-to-br ${data.gradient} rounded-3xl blur-3xl scale-110 opacity-50`} />

          {/* Mockup */}
          <div className="relative">
            <Mockup />
          </div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

export function Features() {
  const { t } = useI18n();

  const features = [
    t.features.items.quality,
    t.features.items.dsat,
    t.features.items.wfm,
    t.features.items.ai,
    t.features.items.quizzes,
  ];

  return (
    <section id="features" className="relative py-24 sm:py-32 lg:py-40">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-muted/30 via-background to-muted/30" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="max-w-4xl mx-auto text-center mb-24 lg:mb-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6"
          >
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">
              {t.features.badge}
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-display font-medium text-foreground tracking-tight mb-6"
          >
            {t.features.title}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto"
          >
            {t.features.subtitle}
          </motion.p>
        </div>

        {/* Feature sections */}
        <div className="space-y-32 lg:space-y-48">
          {features.map((feature, index) => (
            <FeatureSection
              key={feature.title}
              feature={feature}
              index={index}
              isReversed={index % 2 === 1}
            />
          ))}
        </div>

        {/* Bottom stats */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-32 lg:mt-48"
        >
          <div className="relative rounded-[2.5rem] bg-foreground p-10 lg:p-16 overflow-hidden">
            {/* Background decoration */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent" />
            <div className="absolute top-0 right-0 w-96 h-96 bg-primary/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />

            <div className="relative grid sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
              {[
                t.features.stats.channels,
                t.features.stats.parameters,
                t.features.stats.aiAccuracy,
                t.features.stats.timeSaved,
              ].map((stat, i) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 * i }}
                  className="text-center lg:text-left"
                >
                  <div className="text-4xl lg:text-5xl font-display font-medium text-background mb-2">
                    {stat.value}
                  </div>
                  <div className="text-background/90 font-medium mb-1">
                    {stat.label}
                  </div>
                  <div className="text-sm text-background/50">
                    {stat.sublabel}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
